import type { StoryObject } from '@benzinga/advanced-news-manager';
import { SafeError, type SafePromise, type SafeType } from '@benzinga/safe-await';
import type { SimpleNewsQuery } from '@benzinga/internal-news-manager';
import { simplyQueryInternalNews } from '@benzinga/internal-news-manager';
import type { Session } from '@benzinga/session';
import { chunkifyArray } from '@benzinga/utils';

export const SYMBOL_BATCH_SIZE = 150;
export const WATCHLIST_NEWS_LIMIT = 15;
const WATCHLIST_NEWS_TIMEOUT_MS = 12000;
const PUBLIC_NEWS_API_URL = 'https://www.benzinga.com/api/news';

export type WatchlistNewsItem = StoryObject;

export type WatchlistNewsFetchResult =
  | { status: 'ok'; news: WatchlistNewsItem[] }
  | { status: 'error' };

export type PopularNewsFetchResult =
  | { status: 'ok'; news: WatchlistNewsItem[] }
  | { status: 'error' };

export function getWatchlistNewsTimestamp(article: WatchlistNewsItem): string | undefined {
  return article.updated ?? article.created ?? undefined;
}

export function getWatchlistNewsTicker(article: WatchlistNewsItem): string | undefined {
  return article.tickers?.[0]?.name;
}

export function dedupeAndSortWatchlistNews(items: WatchlistNewsItem[], limit: number): WatchlistNewsItem[] {
  const seenIds = new Set<number>();
  const uniqueItems: WatchlistNewsItem[] = [];

  for (const item of items) {
    if (seenIds.has(item.id)) continue;
    seenIds.add(item.id);
    uniqueItems.push(item);
  }

  return uniqueItems
    .sort(
      (left, right) =>
        new Date(getWatchlistNewsTimestamp(right) ?? 0).getTime() -
        new Date(getWatchlistNewsTimestamp(left) ?? 0).getTime(),
    )
    .slice(0, limit);
}

export function mergeWatchlistNewsChunks(
  responses: SafeType<WatchlistNewsItem[]>[],
): SafeType<WatchlistNewsItem[]> {
  const successfulResponses = responses.filter(
    (response): response is { ok: WatchlistNewsItem[]; err?: undefined } => response.ok !== undefined,
  );

  if (successfulResponses.length === 0) {
    return {
      err: responses.find(response => response.err)?.err ?? new SafeError('Watchlist news fetch failed', 'unknown'),
    };
  }

  return {
    ok: dedupeAndSortWatchlistNews(
      successfulResponses.flatMap(response => response.ok),
      WATCHLIST_NEWS_LIMIT,
    ),
  };
}

async function fetchPublicWatchlistNews(
  symbols: string[],
  newsQuery?: SimpleNewsQuery,
): SafePromise<WatchlistNewsItem[]> {
  const symbolChunks =
    symbols.length <= SYMBOL_BATCH_SIZE ? [symbols] : chunkifyArray([...symbols], SYMBOL_BATCH_SIZE);

  const responses = await Promise.all(
    symbolChunks.map(async symbolChunk => {
      const params = new URLSearchParams({
        limit: String(WATCHLIST_NEWS_LIMIT),
        tickers: symbolChunk.join(','),
      });

      if (newsQuery?.channels) {
        const channels = Array.isArray(newsQuery.channels)
          ? newsQuery.channels.join(',')
          : String(newsQuery.channels);
        params.set('channels', channels);
      }

      try {
        const response = await fetch(`${PUBLIC_NEWS_API_URL}?${params.toString()}`, {
          next: { revalidate: 60 },
        });

        if (!response.ok) {
          return {
            err: new SafeError(`Public news API returned HTTP ${response.status}`, 'http'),
          };
        }

        const news = (await response.json()) as WatchlistNewsItem[];
        return { ok: Array.isArray(news) ? news : [] };
      } catch (error) {
        return {
          err: new SafeError(
            error instanceof Error ? error.message : 'Public news API request failed',
            'unknown',
            error,
          ),
        };
      }
    }),
  );

  return mergeWatchlistNewsChunks(responses);
}

async function fetchInternalWatchlistNews(
  session: Session,
  symbols: string[],
  newsQuery?: SimpleNewsQuery,
): SafePromise<WatchlistNewsItem[]> {
  const symbolChunks =
    symbols.length <= SYMBOL_BATCH_SIZE ? [symbols] : chunkifyArray([...symbols], SYMBOL_BATCH_SIZE);

  const responses = await Promise.all(
    symbolChunks.map(symbolChunk => {
      const query = {
        ...(newsQuery ?? {}),
        tickers: symbolChunk.join(','),
      };
      return simplyQueryInternalNews(
        session,
        query,
        { limit: WATCHLIST_NEWS_LIMIT, displayOutput: 'abstract' },
        {
          source:
            symbolChunks.length === 1
              ? 'bz-profile-portfolio:watchlist-news'
              : 'bz-profile-portfolio:watchlist-news-chunk',
          timeoutMs: WATCHLIST_NEWS_TIMEOUT_MS,
        },
      );
    }),
  );

  return mergeWatchlistNewsChunks(responses);
}

export async function fetchWatchlistNewsForPortfolio(
  session: Session,
  symbols: string[],
  newsQuery?: SimpleNewsQuery,
): Promise<WatchlistNewsFetchResult> {
  if (symbols.length === 0) {
    return { status: 'ok', news: [] };
  }

  const internalNews = await fetchInternalWatchlistNews(session, symbols, newsQuery);
  if (internalNews.ok) {
    return { status: 'ok', news: internalNews.ok };
  }

  const publicNews = await fetchPublicWatchlistNews(symbols, newsQuery);
  if (publicNews.ok) {
    return { status: 'ok', news: publicNews.ok };
  }

  return { status: 'error' };
}

export async function fetchPopularNewsForPortfolio(session: Session): Promise<PopularNewsFetchResult> {
  const { BasicNewsManager } = await import('@benzinga/basic-news-manager');
  const response = await session.getManager(BasicNewsManager).getTopPostsUsingChartbeat({ limit: 10 });

  if (response.err) {
    return { status: 'error' };
  }

  return { status: 'ok', news: (response.ok ?? []) as WatchlistNewsItem[] };
}
